import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Money, Truck, ArrowClockwise, DownloadSimple, Warning, CurrencyDollar,
} from "@phosphor-icons/react";
import client from "../lib/api";

const RANGES = [
  { label: "4 wk", value: 4 },
  { label: "8 wk", value: 8 },
  { label: "13 wk (Q)", value: 13 },
  { label: "26 wk (H)", value: 26 },
];

function fmtInt(n) { return Math.round(n || 0).toLocaleString(); }
function fmtUSD(n) { return `$${Math.round(n || 0).toLocaleString()}`; }
function fmtPerMi(n) { return n ? `$${n.toFixed(2)}` : "—"; }
function fmtWeekLabel(w) {
  const s = new Date(w.start || w.week_start);
  return s.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function downloadCSV(data) {
  const wk = data.week_labels || [];
  const header = [
    "Driver", "Loads", "Loaded mi", "Empty mi", "Total mi",
    "Revenue", "$/loaded mi",
    ...wk.flatMap((w) => [
      `${fmtWeekLabel(w)} loaded_mi`,
      `${fmtWeekLabel(w)} revenue`,
      `${fmtWeekLabel(w)} loads`,
    ]),
  ];
  const rows = data.rows.map((r) => {
    const cells = [
      r.driver_name || "",
      r.totals.load_count,
      r.totals.loaded_miles,
      r.totals.empty_miles,
      r.totals.total_miles,
      r.totals.revenue,
      r.totals.per_loaded_mile,
    ];
    for (const w of r.weeks) {
      cells.push(w.loaded_miles, w.revenue, w.load_count);
    }
    return cells;
  });
  const csv = [header, ...rows].map((r) =>
    r.map((v) => (typeof v === "string" && v.includes(",") ? `"${v}"` : v)).join(",")
  ).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `weekly-miles-${data.range_start}-to-${data.range_end}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function WeeklyMilesReportPage() {
  const [weeks, setWeeks] = useState(8);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (w = weeks) => {
    setLoading(true);
    try {
      const { data } = await client.get(`/reports/weekly-miles?weeks=${w}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load miles report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(weeks); /* eslint-disable-next-line */ }, [weeks]);

  const hasAnyData = useMemo(() => {
    if (!data) return false;
    return (data.grand_totals?.load_count || 0) > 0;
  }, [data]);

  return (
    <div className="space-y-6" data-testid="weekly-miles-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-[#FF5A1F] mb-1">MILES REPORT</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Weekly miles by driver
          </h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
            Loaded miles, empty miles, revenue, and $/loaded-mi per driver per week. Only counts loads
            with status <span className="text-neutral-300 font-medium">delivered</span>. Miles are
            auto-calculated for new loads via city coordinates + a 1.20 road factor.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 rounded-full border border-white/10 p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setWeeks(r.value)}
                disabled={loading}
                data-testid={`miles-range-${r.value}`}
                className={`px-3 py-1 rounded-full text-xs transition-colors ${
                  weeks === r.value
                    ? "bg-white/10 text-white"
                    : "text-neutral-400 hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => load(weeks)}
            disabled={loading}
            data-testid="miles-refresh-btn"
            className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 hover:border-white/25 transition-colors disabled:opacity-50"
          >
            <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          {hasAnyData && (
            <button
              onClick={() => downloadCSV(data)}
              data-testid="miles-export-csv"
              className="btn-primary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5"
            >
              <DownloadSimple size={12} weight="bold" /> CSV
            </button>
          )}
        </div>
      </div>

      {/* Grand totals */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="miles-totals">
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Total loaded mi</div>
            <div className="font-display text-3xl font-semibold text-white tabular-nums">
              {fmtInt(data.grand_totals.loaded_miles)}
            </div>
            <div className="text-[10px] text-neutral-600 mt-1">
              +{fmtInt(data.grand_totals.empty_miles)} deadhead
            </div>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1">Revenue</div>
            <div className="font-display text-3xl font-semibold text-emerald-400 tabular-nums">
              {fmtUSD(data.grand_totals.revenue)}
            </div>
            <div className="text-[10px] text-neutral-600 mt-1">
              {data.grand_totals.load_count} delivered load{data.grand_totals.load_count !== 1 ? "s" : ""}
            </div>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] mb-1">$ / loaded mi</div>
            <div className="font-display text-3xl font-semibold text-[#FF5A1F] tabular-nums">
              {fmtPerMi(data.grand_totals.per_loaded_mile)}
            </div>
            <div className="text-[10px] text-neutral-600 mt-1">Weighted average</div>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Active drivers</div>
            <div className="font-display text-3xl font-semibold text-white tabular-nums">
              {data.rows.filter((r) => r.driver_id !== "unassigned" && r.totals.load_count > 0).length}
            </div>
            <div className="text-[10px] text-neutral-600 mt-1">
              {data.range_start} → {data.range_end}
            </div>
          </div>
        </div>
      )}

      {/* Per-driver × per-week matrix */}
      {loading && !data ? (
        <div className="text-sm text-neutral-500 py-16 text-center">Loading…</div>
      ) : !hasAnyData ? (
        <div className="rounded-lg border border-dashed border-white/10 py-12 text-center text-sm text-neutral-500">
          <Truck size={20} className="mx-auto mb-2 text-neutral-600" />
          No delivered loads in this window. Deliver some loads to see miles here.
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 overflow-x-auto" data-testid="miles-matrix">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-neutral-500">
              <tr>
                <th className="text-left px-4 py-3 sticky left-0 bg-[#0F0F0F] z-10">Driver</th>
                <th className="text-right px-3 py-3">Loads</th>
                <th className="text-right px-3 py-3">Loaded mi</th>
                <th className="text-right px-3 py-3">Revenue</th>
                <th className="text-right px-3 py-3">$/mi</th>
                {data.week_labels.map((w) => (
                  <th key={w.key} className="text-right px-3 py-3 min-w-[110px]">
                    <div>{fmtWeekLabel(w)}</div>
                    <div className="text-[9px] text-neutral-600 font-normal normal-case">
                      wk {w.key.split("-W")[1]}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.05]">
              {data.rows.map((r) => (
                <tr key={r.driver_id} className="hover:bg-white/[0.02]"
                    data-testid={`miles-row-${r.driver_id}`}>
                  <td className="text-left px-4 py-3 sticky left-0 bg-[#0F0F0F]">
                    <div className="font-medium text-white truncate max-w-[180px]">
                      {r.driver_name}
                    </div>
                    {r.driver_email && (
                      <div className="text-[10px] text-neutral-500 font-mono truncate max-w-[180px]">
                        {r.driver_email}
                      </div>
                    )}
                    {r.driver_id === "unassigned" && (
                      <div className="text-[10px] text-orange-400 inline-flex items-center gap-1 mt-0.5">
                        <Warning size={10} weight="fill" /> No driver assigned
                      </div>
                    )}
                  </td>
                  <td className="text-right px-3 py-3 tabular-nums text-neutral-300">
                    {r.totals.load_count}
                  </td>
                  <td className="text-right px-3 py-3 tabular-nums font-medium text-white">
                    {fmtInt(r.totals.loaded_miles)}
                  </td>
                  <td className="text-right px-3 py-3 tabular-nums font-medium text-emerald-400">
                    {fmtUSD(r.totals.revenue)}
                  </td>
                  <td className="text-right px-3 py-3 tabular-nums text-[#FF5A1F]">
                    {fmtPerMi(r.totals.per_loaded_mile)}
                  </td>
                  {r.weeks.map((w) => (
                    <td key={w.week_key} className="text-right px-3 py-3 tabular-nums text-neutral-400">
                      {w.loaded_miles > 0 ? (
                        <div>
                          <div className="text-white">{fmtInt(w.loaded_miles)} mi</div>
                          <div className="text-[10px] text-emerald-400/70">{fmtUSD(w.revenue)}</div>
                        </div>
                      ) : (
                        <span className="text-neutral-700">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              {/* Grand-total row */}
              <tr className="bg-white/[0.03] border-t-2 border-white/10 font-semibold">
                <td className="text-left px-4 py-3 sticky left-0 bg-[#141414]">
                  <div className="inline-flex items-center gap-1.5">
                    <CurrencyDollar size={13} weight="fill" className="text-[#FF5A1F]" />
                    Total
                  </div>
                </td>
                <td className="text-right px-3 py-3 tabular-nums">{data.grand_totals.load_count}</td>
                <td className="text-right px-3 py-3 tabular-nums">
                  {fmtInt(data.grand_totals.loaded_miles)}
                </td>
                <td className="text-right px-3 py-3 tabular-nums text-emerald-400">
                  {fmtUSD(data.grand_totals.revenue)}
                </td>
                <td className="text-right px-3 py-3 tabular-nums text-[#FF5A1F]">
                  {fmtPerMi(data.grand_totals.per_loaded_mile)}
                </td>
                {data.week_labels.map((w) => {
                  const wkTotal = data.rows.reduce((acc, r) => {
                    const ww = r.weeks.find((x) => x.week_key === w.key);
                    return acc + (ww?.loaded_miles || 0);
                  }, 0);
                  return (
                    <td key={w.key} className="text-right px-3 py-3 tabular-nums">
                      {wkTotal > 0 ? `${fmtInt(wkTotal)} mi` : "—"}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
